import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '@/app/store'
import { TopBar, SearchBarSection } from '@/components/home'
import DateFilterButton from '../components/orders/DateFilterButton'
import OrdersGrid from '../components/orders/OrdersGrid'
import OrdersGridSkeleton from '../components/orders/OrdersGridSkeleton'
import ConfirmPaymentModal from '../components/orders/ConfirmPaymentModal'
import { Order } from '@/types'
import { fetchCashierOrders, CashierOrdersParams, confirmPayment } from '../services/ordersService'
import { isOrderFromBranch } from '../utils/branchUtils'

interface CollectionPageProps {
  className?: string
}

const formatDateParam = (date?: Date | string | null): string | undefined => {
  if (!date) return undefined
  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd')
  }
  if (typeof date === 'string') {
    return date.split('T')[0]
  }
  return undefined
}

const COLLECTION_TYPE_IDS = [2, 3] as const
const COLLECTION_STATUS_IDS = [1, 2] as const

type CollectionTypeFilter = 'all' | 2 | 3

const CollectionPage: React.FC<CollectionPageProps> = ({ className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Redux state
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)

  // Local state
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<CollectionTypeFilter>('all')
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState<{
    startDate: Date | string | null
    endDate: Date | string | null
  }>({
    startDate: null,
    endDate: null,
  })
  
  // Scroll container ref
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Confirm payment modal state
  const [confirmPaymentModalOpen, setConfirmPaymentModalOpen] = useState(false)
  const [orderToConfirmPayment, setOrderToConfirmPayment] = useState<Order | null>(null)
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)

  const isRTL = settings.language === 'ar'

  // Get the branch the cashier is logged in with (same logic as HomePage)
  const cashierBranchId = useMemo(() => {
    // Priority 1: branch ID from login data
    if (user?.branch?.id) {
      const branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(branchId) && branchId > 0) {
        return branchId
      }
    }
    // Fallback: branch ID from localStorage employee_data
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.id) {
          const branchId = typeof employeeData.branch.id === 'number' ? employeeData.branch.id : Number(employeeData.branch.id)
          if (!isNaN(branchId) && branchId > 0) {
            return branchId
          }
        }
      }
    } catch (e) {
      // ignore
    }
    return undefined
  }, [user?.branch?.id])

  const cashierBranchName = useMemo(() => {
    if (user?.branch?.name) return user.branch.name
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.name) return String(employeeData.branch.name)
      }
    } catch (e) {
      // ignore
    }
    return undefined
  }, [user?.branch?.name])

  const isOrderFromCashierBranch = useCallback(
    (order: Order) => isOrderFromBranch(order, { id: cashierBranchId, name: cashierBranchName }),
    [cashierBranchId, cashierBranchName]
  )

  // Fetch orders function - with fixed typeId filter for collection
  const loadOrders = useCallback(async (page: number = 1, append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
      setError(null)
      setOrders([])
    }

    try {
      const params: CashierOrdersParams = {
        // typeId 2 (takeaway/استلام) or 3 (dine_in/في الفرع), or both when "all"
        typesArr:
          selectedTypeFilter === 'all'
            ? [...COLLECTION_TYPE_IDS]
            : [selectedTypeFilter],
        statusesArr: [...COLLECTION_STATUS_IDS],
        page,
      }

      // Filter by the cashier's branch (the branch they opened the shift with)
      if (cashierBranchId) {
        params.branchId = cashierBranchId
      }

      // Add date filters
      const startDateParam = formatDateParam(dateFilter.startDate)
      if (startDateParam) {
        params.dateFrom = startDateParam
      }
      
      const endDateParam = formatDateParam(dateFilter.endDate)
      if (endDateParam) {
        params.dateTo = endDateParam
      }

      const trimmedSearch = searchQuery.trim()
      if (trimmedSearch) {
        params.search = trimmedSearch
      }

      const response = await fetchCashierOrders(params, append)
      
      if (append) {
        setOrders(prev => {
          const newOrders = [...prev, ...response.orders]
          return newOrders
        })
      } else {
        setOrders(response.orders)
      }
      
      setCurrentPage(response.meta.currentPage)
      const hasMorePages = response.meta.currentPage < response.meta.lastPage
      setHasMore(hasMorePages)
    } catch (err: any) {
      if (err?.response?.status === 401 || err?.status === 401) {
        setError(null)
      } else {
        let userFriendlyMessage = t('common.error') || 'حدث خطأ في تحميل الطلبات'
        
        const status = err?.response?.status || err?.status
        const errorMessage = err?.message || ''
        
        if (status === 500) {
          userFriendlyMessage = t('error.server_error') || 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى'
        } else if (status === 403) {
          userFriendlyMessage = t('error.unauthorized_error') || 'غير مصرح لك بالوصول إلى هذه البيانات'
        } else if (status === 404) {
          userFriendlyMessage = t('error.not_found') || 'البيانات المطلوبة غير موجودة'
        } else if (status === 422) {
          userFriendlyMessage = t('error.validation_error') || 'البيانات المرسلة غير صحيحة'
        } else if (errorMessage.includes('Network Error') || errorMessage.includes('network')) {
          userFriendlyMessage = t('error.network_error') || 'خطأ في الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت'
        } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
          userFriendlyMessage = t('error.timeout_error') || 'انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى'
        } else if (errorMessage.includes('Request failed with status code')) {
          userFriendlyMessage = t('error.server_error') || 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى'
        } else if (err?.response?.data?.message) {
          userFriendlyMessage = err.response.data.message
        } else if (errorMessage && !errorMessage.includes('status code')) {
          userFriendlyMessage = errorMessage
        }
        
        setError(userFriendlyMessage)
      }
      setHasMore(false)
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [cashierBranchId, dateFilter, searchQuery, selectedTypeFilter, t])

  const typeFilterOptions: Array<{ value: CollectionTypeFilter; label: string }> = [
    
    { value: 2, label: t('orders.type.takeaway') },
    { value: 3, label: t('orders.type.dine_in') },
    { value: 'all', label: t('orders.filter.all_types') },
  ]

  // Function to load more orders (for scroll loading)
  const fetchMoreData = useCallback(() => {
    if (!isLoadingMore && hasMore && !isLoading) {
      const nextPage = currentPage + 1
      loadOrders(nextPage, true)
    }
  }, [currentPage, hasMore, isLoadingMore, isLoading, loadOrders])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore) return

    const loadMoreElement = loadMoreRef.current
    const scrollContainer = scrollContainerRef.current
    
    if (!loadMoreElement || !scrollContainer) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (
          entry.isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          fetchMoreData()
        }
      },
      {
        root: scrollContainer,
        rootMargin: '0px',
        threshold: 0,
      }
    )

    const timeoutId = setTimeout(() => {
      if (loadMoreElement) {
        observer.observe(loadMoreElement)
      }
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [hasMore, isLoading, isLoadingMore, fetchMoreData, orders.length])

  // Initial load and when filters change
  useEffect(() => {
    setCurrentPage(1)
    setHasMore(true)
    loadOrders(1, false)
  }, [loadOrders, settings.language])

  // Check if order is eligible for payment confirmation
  const canConfirmPayment = (order: Order): boolean => {
    const isCashPayment = order.payment_method === 'cash'
    const isCollectionType = order.order_type === 'takeaway' || order.order_type === 'dine_in'
    const isPaymentPending = order.payment_status === 'pending' || order.payment_status === 'partial'
    return isCashPayment && isCollectionType && isPaymentPending && isOrderFromCashierBranch(order)
  }

  const handleOrderClick = (order: Order) => {
    navigate(`/orders/${order.id}`, { state: { from: '/collection' } })
  }

  const handleDateChange = (value: {
    startDate: Date | string | null
    endDate: Date | string | null
  }) => {
    setDateFilter(value)
  }

  const handleDateClear = () => {
    setDateFilter({
      startDate: null,
      endDate: null,
    })
  }

  const handleConfirmPayment = async () => {
    if (!orderToConfirmPayment) return

    setIsConfirmingPayment(true)
    try {
      await confirmPayment(orderToConfirmPayment.id)
      // Refresh orders list after successful confirmation
      loadOrders(currentPage, false)
      setConfirmPaymentModalOpen(false)
      setOrderToConfirmPayment(null)
    } catch (err: any) {
      console.error('Failed to confirm payment:', err)
      // Error handling could be improved with toast notification
    } finally {
      setIsConfirmingPayment(false)
    }
  }

  const handleOpenConfirmPayment = (order: Order) => {
    if (canConfirmPayment(order)) {
      setOrderToConfirmPayment(order)
      setConfirmPaymentModalOpen(true)
    }
  }

  const visibleOrders = orders

  return (
    <div className={`w-full h-screen bg-neutral-100 flex flex-col overflow-hidden ${className}`}>
      <div className={`${isRTL ? 'pl-6 pt-2' : 'pr-6 pt-5'}`}>
        {/* Top Bar */}
        <TopBar userName={user?.name} />
      </div>
      <div className="self-stretch flex-1 px-6 pt-4 relative flex flex-col gap-2 min-h-0">
        {/* Search and Date Filters */}
        <div className="self-stretch flex flex-col gap-4">
          {/* Search, Type Filters, and Date Filters Row */}
          <div className="flex flex-row justify-between items-center gap-3">
            <div className="flex flex-row items-center gap-3 flex-1 min-w-0">
            <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {typeFilterOptions.map((option) => {
                  const isActive = selectedTypeFilter === option.value
                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => setSelectedTypeFilter(option.value)}
                      className={`
                        h-10 px-4 py-1 rounded-xl flex items-center gap-2 transition-all whitespace-nowrap
                        ${isActive
                          ? 'bg-flutter-button shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.25)]'
                          : 'bg-white'
                        }
                      `}
                    >
                      <span
                        className={`
                          text-sm font-medium font-['IBM_Plex_Sans_Arabic']
                          ${isActive ? 'text-text-primary-on-accent' : 'text-text-primary'}
                        `}
                      >
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </div>
          
              <SearchBarSection
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder={t('common.search')}
                className="min-w-[200px] max-w-[320px] flex-shrink-0"
              />
            
            </div>
            
            <DateFilterButton 
              value={dateFilter}
              onChange={handleDateChange}
              onClear={handleDateClear}
              isRTL={isRTL}
            />
          </div>
        </div>

        {/* Orders Grid - Scrollable Container with manual scroll loading */}
        <div 
          ref={scrollContainerRef} 
          className="self-stretch flex-1 overflow-y-auto min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style]:none [scrollbar-width]:none"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            position: 'relative',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            flex: '1 1 0%'
          }}
        >
          {isLoading ? (
            <div className="flex flex-col w-full">
              <OrdersGridSkeleton count={6} />
            </div>
          ) : error ? (
            <div className="flex flex-col justify-center items-center h-full gap-4 px-4">
              <div className="text-center max-w-md">
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <p className={`text-red-600 text-lg font-semibold mb-2 font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('error.something_went_wrong') || 'حدث خطأ'}
                </p>
                <p className={`text-gray-700 text-base font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                  {error}
                </p>
                <button
                  onClick={() => {
                    setError(null)
                    loadOrders(1, false)
                  }}
                  className={`mt-4 px-6 py-2 bg-flutter-button text-white rounded-lg font-medium hover:bg-flutter-button/90 transition-colors font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'ml-auto' : 'mr-auto'}`}
                >
                  {t('common.retry') || 'إعادة المحاولة'}
                </button>
              </div>
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-text-secondary">{t('orders.no_orders')}</p>
            </div>
          ) : (
            <>
              <OrdersGrid
                orders={visibleOrders}
                onOrderClick={handleOrderClick}
                isRTL={isRTL}
                showLoadingIndicator={isLoadingMore && hasMore}
                onConfirmPayment={handleOpenConfirmPayment}
                canConfirmPayment={isOrderFromCashierBranch}
              />
              {hasMore && <div ref={loadMoreRef} style={{ height: '20px', width: '100%' }} />}
              {!hasMore &&
                visibleOrders.length > 0 &&
                visibleOrders.length !== 1 &&
                visibleOrders.length !== 6 && (
                <div className="flex justify-center items-center py-6 mt-4">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                    <p className="text-xs text-gray-500 font-['IBM_Plex_Sans_Arabic'] font-medium">
                      {t('orders.no_more_orders', 'لا توجد المزيد من الطلبات')}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm Payment Modal */}
      <ConfirmPaymentModal
        isOpen={confirmPaymentModalOpen}
        isRTL={isRTL}
        isLoading={isConfirmingPayment}
        onClose={() => {
          setConfirmPaymentModalOpen(false)
          setOrderToConfirmPayment(null)
        }}
        onConfirm={handleConfirmPayment}
      />
    </div>
  )
}

export default CollectionPage